/**
 * Spacing constants for consistent layout across all dashboards
 *
 * Based on Tailwind's spacing scale:
 * - 1 = 4px (tight spacing)
 * - 2 = 8px (icon containers, very tight gaps)
 * - 3 = 12px (small gaps, list items)
 * - 4 = 16px (grid gaps, card sections)
 * - 5 = 20px (compact cards)
 * - 6 = 24px (section spacing, standard cards, headers)
 * - 8 = 32px (large section spacing)
 */

export const DASHBOARD_SPACING = {
  /**
   * Vertical spacing between dashboard sections
   * Applied to root container's space-y class
   */
  SECTION_GAP: 'gap-6', // 24px

  /**
   * Horizontal spacing in grid layouts (stat cards, quick actions)
   * Applied to grid gap property
   */
  GRID_GAP: 'gap-4', // 16px

  /**
   * Smaller grid gap for dense layouts (content type badges, access levels)
   */
  GRID_GAP_DENSE: 'gap-3', // 12px

  /**
   * Tight gap for small items (icon + text pairs)
   */
  GAP_TIGHT: 'gap-2', // 8px

  /**
   * Standard padding for dashboard cards
   * Applied to card containers
   */
  CARD_PADDING: 'p-6', // 24px

  /**
   * Compact padding for smaller cards
   * Applied to stat cards, quick action buttons
   */
  CARD_PADDING_COMPACT: 'p-5', // 20px

  /**
   * Dense padding for content items within cards
   * Applied to list items, content rows
   */
  CARD_PADDING_DENSE: 'p-4', // 16px

  /**
   * Extra dense padding for small items (badges, type indicators)
   */
  CARD_PADDING_XDENSE: 'p-3', // 12px

  /**
   * Icon container padding (for icons inside cards)
   */
  ICON_PADDING: 'p-2', // 8px

  /**
   * Spacing below section headers
   * Applied to header container's mb class
   */
  HEADER_MARGIN_BOTTOM: 'mb-6', // 24px

  /**
   * Spacing below subsection headers
   * Applied to subsection title's mb class
   */
  SUBHEADER_MARGIN_BOTTOM: 'mb-4', // 16px

  /**
   * Small margin for spacing between related items
   */
  MARGIN_SMALL: 'mb-3', // 12px

  /**
   * Tiny margin for very tight spacing
   */
  MARGIN_TINY: 'mb-2', // 8px

  /**
   * Minimum margin (stat value to label)
   */
  MARGIN_MIN: 'mb-1', // 4px

  /**
   * Spacing between list items
   * Applied to space-y within content lists
   */
  LIST_ITEM_GAP: 'gap-3', // 12px

  /**
   * Spacing between icon and text in cards
   * Applied to flex items with icon + text
   */
  ICON_TEXT_GAP: 'gap-3', // 12px

  /**
   * Tight icon-text gap for compact layouts
   */
  ICON_TEXT_GAP_TIGHT: 'gap-2', // 8px

  /**
   * Button padding (standard size buttons)
   */
  BUTTON_PADDING: 'px-4 py-2', // horizontal 16px, vertical 8px

  /**
   * Spacing between stat value and label
   * Applied to mb class for stat containers
   */
  STAT_LABEL_GAP: 'mb-1', // 4px
} as const

/**
 * Helper function to get spacing class for a specific use case
 */
export const getSpacingClass = (type: keyof typeof DASHBOARD_SPACING): string => {
  return DASHBOARD_SPACING[type]
}

/**
 * Standard dashboard container classes
 */
export const DASHBOARD_CONTAINER_CLASSES = 'space-y-6 overflow-x-hidden'

/**
 * Standard card classes
 */
export const CARD_CLASSES = 'bg-card border border-border rounded-lg'

/**
 * Standard section header classes
 */
export const SECTION_HEADER_CLASSES = 'flex items-center gap-3 mb-6'
